var Type = require('./Type');

/**
 * The Numeric class represents a numeric data type. Numeric data can
 * handle several options like thousands delimiter, decimal (scale)
 * and different types like 'percent' and 'exponential'.
 *
 * @param {Object} [oConfig] - Configuration object for this numeric representation
 * @param {boolean} [oConfig.delimiter] - Indicates if a thousands delimiter should be used
 * @param {number} [oConfig.scale] - Defines the amount of decimals
 * @param {string} [oConfig.unit] - Specifies a particular unit of measurement
 * @param {string} [oConfig.unitProperty] - Specifies a property that contains the unit of measurement
 * @constructor
 */
function Numeric(oConfig) {
	Type.call(this, oConfig);

	this.unit = oConfig ? oConfig.unit : null;
	this.unitProperty = oConfig ? oConfig.unitProperty : null;
	this.delimiter = oConfig ? oConfig.delimiter : false;
	this.scale = oConfig ? oConfig.scale : null;

	this.styleIds = {};
}

Numeric.prototype = Object.create(Type.prototype);
Numeric.prototype.constructor = Numeric;

Numeric.DEFAULT = {
	numberFormat: 0
};

Numeric.builtin = {
	1: '0',
	2: '0.00',
	3: '#,##0',
	4: '#,##0.00',
	9: '0%',
	10: '0.00%',
	11: '0.00E+00',
	12: '# ?/?',
	13: '# ??/??',
	37: '#,##0;(#,##0)',
	38: '#,##0;[Red](#,##0)',
	39: '#,##0.00;(#,##0.00)',
	40: '#,##0.00;[Red](#,##0.00)',
	48: '##0.0E+0'
};

Numeric.scientificRegEx = /^-?\d?(\.\d)?\d*[eE][+-]?\d{1,3}$/;

/**
 * Creates the format according to the provided configuration. If the
 * format matches any builtin format, it will return a reference to
 * its ID instead of the format.
 *
 * Both cases can serve the Style class that consumes this information.
 *
 * @param {Object} oData - Business object that contains additional data that is used for the formatting
 * @returns {Object} - Configuration object for NumberFormat initialization
 * @protected
 */
Numeric.prototype._createConfiguration = function(oData) {
	var config, i, key,	template, unit;

	config = Object.getPrototypeOf(Numeric.prototype)._createConfiguration.call(this);

	/*
	 * Evaluate unit of measurement first because a plain built-in
	 * format is used as template but we need to be able to return
	 * to the DEFAULT value in case the unit is empty or invalid.
	 */

	/* Obtain unit of measurement if available */
	if (typeof this.unitProperty === 'string' && oData) {
		unit = oData[this.unitProperty];
	} else if (typeof this.unit === 'string') {
		unit = this.unit;
	}

	if (typeof unit !== 'string' || unit === '"') {
		unit = '';
	}

	if (!this.delimiter && typeof this.scale !== 'number' && !unit) {
		config.numberFormat = Numeric.DEFAULT.numberFormat;

		return config;
	}

	// Use built-in template as base
	template = Numeric.builtin[1];

	if (this.delimiter) {
		template = '#,##' + template;
	}

	/* Add decimal*/
	if (typeof this.scale === 'number' && this.scale > 0) {
		template += '.';

		for (i = 0; i < this.scale; i++) {
			template += '0';
		}
	}

	/* Apply unit if present */
	if (unit) {
		template += '\\ "' + unit + '"';
	}

	config.numberFormat = {
		format: template
	};

	/* Find possible builtin match */
	for (key in Numeric.builtin) {
		if (Numeric.builtin[key] === template) {
			config.numberFormat = parseInt(key, 10);
		}
	}

	return config;
};

/**
 * Returns the assigned ID of the Type specific Style that is
 * registered in the Styles class.
 *
 * @param {Object} oData - Business Object that contains the property for the UoM
 * @param {string|number} [value] - Explicit cell value that might be used for semantic formatting
 * @returns {number} - ID of the registered Style
 */
Numeric.prototype.getStyleId = function(oData, value) {
	var styleId, unit;

	/* Handle simple numeric without unitProperty */
	if (!this.unitProperty) {
		return Object.getPrototypeOf(Numeric.prototype).getStyleId.call(this);
	}

	if (!oData) {
		return 0;
	}

	unit = oData[this.unitProperty];
	styleId = this.styleIds[unit];

	if (typeof styleId === 'undefined') {
		this.styleIds[unit] = this.styles.addStyle(this._createConfiguration(oData));
	}

	return this.styleIds[unit];
};

/**
 * Returns the data value of the business object that is bound by
 * this type. The value is normalized so that different input
 * representations will result in the same format.
 *
 * @param {string} sProperty - Name of the property that shall be accessed
 * @param {Object} oData - Business object that contains the data
 *
 * @returns {string|number|boolean|null} - The value of the given property after type specific processing.
 */
Numeric.prototype.getValue = function(sProperty, oData) {
	var aSegments, iScale, iExponent, sValue = oData[sProperty];

	if (typeof sValue == 'string' && Numeric.scientificRegEx.test(sValue)) {

		aSegments = sValue.split(/\.|[eE]/g);
		iScale = aSegments.length === 3 ? aSegments[1].length : 0;
		iExponent = Number(aSegments[aSegments.length - 1]);

		/* According to OData V4 Edm.Double is an IEEE 754 binary64 floating-point number (15-17 decimal digits) */
		sValue = Number(sValue).toFixed(Math.max(iScale - iExponent, 0));

		/* Write back the transformed value to avoid subsequent transformations */
		oData[sProperty] = sValue;
	}

	return sValue;
};

/**
 * Checks if the given value can be displayed in the IEEE 754
 * specification format. The function ignores leading and trailing
 * zeros and compares the length of the actual value does not exceed
 * 15 digits plus an optional decimal separator.
 *
 * The number might exceed 15 digits if it contains leading or
 * trailing zeros.
 *
 * @param {string} sValue - Numeric value that needs to be checked
 * @returns {boolean} - Whether the number needs to be formatted as BigNumber or not
 */
Numeric.prototype.isIEEE754Compliant = function(sValue) {
	var bCompliant = false;

	if (typeof sValue == 'number') {
		sValue = '' + sValue;
	}

	if (typeof sValue == 'string') {

		bCompliant = sValue
			.replace(/^-*0*|0+$/g, '')
			.split('.')
			.reduce(function(acc, value) {

				/*
				 * Returns 1 if the value left of the separator is
				 * zero and was cut of by removing leading and trailing zeros
				 */
				return (acc + value.length) || 1;
			}, 0) <= 15;
	}

	return bCompliant;
};

module.exports = Numeric;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/binding/type/Numeric.js
// module id = 5
// module chunks = 0